import { css, html, LitElement, type TemplateResult } from "lit";
import { SignalWatcher } from "@lit-labs/signals";
import { customElement, property } from "lit/decorators.js";
import * as Styles from "../../styles.ts";
import { repeat } from "lit/directives/repeat.js";
import { type ProjectItem } from "../../../models/project.ts";
import { type AppStore } from "../app-store.ts";
import * as Config from "../../client-config.ts";
import { renderRoute } from "../../../util/route.ts";
import { getTheme, toggleTheme } from "../../services/theme.ts";
import "../dui/sidebar/dui-sidebar.ts";
import "../dui/sidebar/dui-sidebar-header.ts";
import "../dui/sidebar/dui-sidebar-footer.ts";
import "../dui/sidebar/dui-sidebar-content.ts";
import "../dui/sidebar/dui-sidebar-group.ts";
import "../dui/sidebar/dui-sidebar-group-label.ts";
import "../dui/sidebar/dui-sidebar-menu.ts";
import "../dui/sidebar/dui-sidebar-menu-item.ts";
import "../dui/sidebar/dui-sidebar-menu-button.ts";
import "../dui/sidebar/dui-sidebar-separator.ts";
import "../dui/icon/dui-icon.ts";
import "../dui/popover/dui-popover.ts";
import "../dui/popover/dui-popover-trigger.ts";
import "../dui/popover/dui-popover-popup.ts";
import "../dui/menu/dui-menu-item.ts";
import "../dui/button/dui-button.ts";
import "../app/app-logo.ts";
import "../app/acct-menu.ts";
import "../app/project-menu.ts";
import "../dui/link/dui-link.ts";

const projectItemView = (
  project: ProjectItem,
  store: AppStore,
): TemplateResult => {
  const href = renderRoute(Config.PROJECT_URL, { project: project.id });
  return html`
    <dui-sidebar-menu-item>
      <dui-sidebar-menu-button tooltip="${project.title}" href="${href}">
        <span>${project.title}</span>
        <div slot="suffix" class="project-actions">
          <project-menu
            .projectId="${project.id}"
            .store="${store}"
          ></project-menu>
        </div>
      </dui-sidebar-menu-button>
    </dui-sidebar-menu-item>
  `;
};

const styles = css`
  :host {
    display: block;
  }

  .logomenu {
    display: block;
    padding: var(--space-6) var(--space-6);
    cursor: pointer;
    text-decoration: none;
    color: inherit;
  }

  .project-actions {
    flex-shrink: 0;
    opacity: 0;
    transition: opacity var(--duration-fast) var(--ease-out-3);
  }

  /* .project-actions is slotted into dui-sidebar-menu-button but remains
     in this component's light DOM, so these selectors can reach it. */
  dui-sidebar-menu-button:hover .project-actions,
  dui-sidebar-menu-button:has(dui-menu[open]) .project-actions {
    opacity: 1;
  }

  dui-sidebar-header dui-sidebar-menu-button {
    --menu-btn-icon-color: color-mix(
      in oklch,
      var(--foreground) 80%,
      transparent
    );
    --menu-btn-weight: var(--font-weight-medium);
  }

  .project-actions dui-button {
    --button-fg: var(--sidebar-button-fg);
    --button-bg: transparent;
    --button-border: transparent;
    --button-hover-bg: color-mix(
      in oklch,
      var(--sidebar-button-bg) 95%,
      var(--foreground)
    );
    --button-hover-fg: var(--sidebar-button-fg);
    --button-hover-border: transparent;
  }
`;

@customElement("sidebar-view")
export class SidebarViewElement extends SignalWatcher(LitElement) {
  static override styles = [Styles.base, styles];

  @property({ attribute: false })
  accessor store: AppStore | undefined = undefined;

  #onNewProjectClick = (event: MouseEvent): void => {
    event.preventDefault();
    this.store?.send({
      type: "toggle-create-project-modal",
      value: true,
    });
  };

  #onSignOutClick = (event: MouseEvent): void => {
    event.preventDefault();
    this.store?.send({ type: "sign-out" });
  };

  #onThemeToggle = (event: MouseEvent): void => {
    event.preventDefault();
    toggleTheme();
  };

  override render(): TemplateResult {
    if (!this.store) {
      return html``;
    }

    const state = this.store.get();
    const isDark = getTheme() === "dark";

    return html`
      <dui-sidebar>
        <dui-sidebar-header>
          <dui-link class="logomenu" href="${Config.BASE_PATH}">
            <app-logo></app-logo>
          </dui-link>
          <dui-sidebar-separator></dui-sidebar-separator>
          <dui-sidebar-group>
            <dui-sidebar-menu>
              <dui-sidebar-menu-item>
                <dui-sidebar-menu-button
                  tooltip="New project"
                  @click="${this.#onNewProjectClick}"
                >
                  <dui-icon slot="icon" icon="add_ad"></dui-icon>
                  <span>New project</span>
                </dui-sidebar-menu-button>
              </dui-sidebar-menu-item>
              <dui-sidebar-menu-item>
                <dui-sidebar-menu-button tooltip="Search">
                  <dui-icon slot="icon" icon="search"></dui-icon>
                  <span>Search</span>
                </dui-sidebar-menu-button>
              </dui-sidebar-menu-item>
              <dui-sidebar-menu-item>
                <dui-sidebar-menu-button
                  tooltip="Library"
                  href="${Config.LIBRARY_URL}"
                >
                  <dui-icon slot="icon" icon="library_books"></dui-icon>
                  <span>Library</span>
                </dui-sidebar-menu-button>
              </dui-sidebar-menu-item>
            </dui-sidebar-menu>
          </dui-sidebar-group>
          <dui-sidebar-separator></dui-sidebar-separator>
        </dui-sidebar-header>
        <dui-sidebar-content>
          <dui-sidebar-group>
            <dui-sidebar-group-label>Projects</dui-sidebar-group-label>
            <dui-sidebar-menu>
              ${repeat(
                state.projects,
                (project) => project.id,
                (project) => projectItemView(project, this.store!),
              )}
            </dui-sidebar-menu>
          </dui-sidebar-group>
        </dui-sidebar-content>
        <dui-sidebar-footer>
          <dui-sidebar-separator style="--inset: 0"></dui-sidebar-separator>
          <dui-popover side="top">
            <dui-popover-trigger>
              <acct-menu
                .username="${state.session?.user?.email ?? "Guest"}"
                type="User"
              ></acct-menu>
            </dui-popover-trigger>
            <dui-popover-popup show-arrow close-on-click>
              <dui-menu-item @click="${this.#onThemeToggle}">
                <dui-icon
                  icon="${isDark ? "light_mode" : "dark_mode"}"
                  size="var(--space-4)"
                ></dui-icon>
                ${isDark ? "Light mode" : "Dark mode"}
              </dui-menu-item>
              <dui-menu-item @click="${this.#onSignOutClick}"
                >Sign out</dui-menu-item
              >
            </dui-popover-popup>
          </dui-popover>
        </dui-sidebar-footer>
      </dui-sidebar>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "sidebar-view": SidebarViewElement;
  }
}
